import { Controller } from 'stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { youtubeVideoId } from '../util';

const VALIDATION_DELAY = 500;

export default class extends Controller {
  static targets = ['sourceRadio', 'urlInput', 'urlError'];

  static values = { previewUrl: String };

  #validityTimeout;

  disconnect() {
    clearTimeout(this.#validityTimeout);
  }

  // Open the client welcome-video preview in a modal, resolved server-side from the
  // card's current unsaved source/URL. The embed id (incl. the custom->default
  // fallback) is baked into the rendered iframe, so no client-side swap is needed.
  async preview() {
    const url = new URL(this.previewUrlValue, window.location.origin);
    url.searchParams.set('welcome_video_source', this.customSelected ? 'custom' : 'default');
    if (this.customSelected) {
      url.searchParams.set('welcome_video_url', this.urlInputTarget.value);
    }

    const response = await fetch(url, {
      headers: { Accept: 'text/vnd.turbo-stream.html' },
      credentials: 'same-origin',
    });

    // A redirect (session expired or the flag turned off) yields an HTML page rather
    // than a stream; fall back to a full navigation instead of feeding non-stream
    // markup to Turbo.
    const contentType = response.headers.get('content-type') || '';
    if (response.redirected || !contentType.includes('turbo-stream')) {
      Turbo.visit(response.url);
      return;
    }

    Turbo.renderStreamMessage(await response.text());
  }

  sourceChanged() {
    this.flagInvalid(false);
    clearTimeout(this.#validityTimeout);
  }

  urlChanged() {
    this.flagInvalid(false);
    clearTimeout(this.#validityTimeout);
    this.#validityTimeout = setTimeout(() => this.refreshValidity(), VALIDATION_DELAY);
  }

  // Flag the URL input when Custom is selected but the value is blank or not a
  // YouTube video URL; aria-invalid carries the error styling.
  refreshValidity() {
    this.flagInvalid(this.customSelected && !youtubeVideoId(this.urlInputTarget.value));
  }

  flagInvalid(invalid) {
    if (invalid) {
      this.urlInputTarget.setAttribute('aria-invalid', 'true');
      this.urlInputTarget.setAttribute('aria-describedby', this.urlErrorTarget.id);
    } else {
      this.urlInputTarget.removeAttribute('aria-invalid');
      this.urlInputTarget.removeAttribute('aria-describedby');
    }
    this.urlErrorTarget.classList.toggle('hidden', !invalid);
    this.element.dataset.dirtyFormBlockedValue = invalid;
  }

  get customSelected() {
    const checked = this.sourceRadioTargets.find((radio) => radio.checked);
    return checked?.value === 'custom';
  }
}
